import { useEffect, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import KButton from '../components/KButton'
import { apiRequest, showKAlert } from '../helpers'
import { t } from '../locale'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'

interface EmailBrandingForm {
  emailHeader: string
  emailSignature: string
  logo: string
}

const initialForm: EmailBrandingForm = {
  emailHeader: '',
  emailSignature: '',
  logo: '',
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function getPlainTextLength(html: string) {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').length
}

function TiptapToolbar({ editor }: { editor: any }) {
  if (!editor) return null
  return (
    <div className='tiptap-toolbar border rounded-top px-2 py-1 d-flex gap-1 flex-wrap bg-light'>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('bold') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <b>B</b>
      </button>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('italic') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <i>I</i>
      </button>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('underline') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <u>U</u>
      </button>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('orderedList') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1.
      </button>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('bulletList') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        •
      </button>
      <button
        type='button'
        className={`btn btn-sm ${editor.isActive('link') ? 'btn-secondary' : 'btn-outline-secondary'}`}
        onClick={() => {
          const url = window.prompt('URL', editor.getAttributes('link').href ?? '')
          if (url === null) return
          if (url === '') {
            editor.chain().focus().unsetLink().run()
          } else {
            const href =
              url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:')
                ? url
                : 'https://' + url
            editor.chain().focus().setLink({ href }).run()
          }
        }}
      >
        🔗
      </button>
      <input
        type='color'
        title='Text color'
        className='form-control form-control-sm p-0 border-0'
        style={{ width: 32, height: 31, cursor: 'pointer' }}
        onChange={e => editor.chain().focus().setColor(e.target.value).run()}
      />
    </div>
  )
}

export default function EmailBrandingSection() {
  const [form, setForm] = useState<EmailBrandingForm>(initialForm)
  const [logoFileName, setLogoFileName] = useState('')
  const [loading, setLoading] = useState(false)
  const [editorReady, setEditorReady] = useState(false)

  const logoInputRef = useRef<HTMLInputElement>(null)

  const editor = useEditor({
    extensions: [StarterKit, Underline, TextStyle, Color, Link.configure({ openOnClick: false })],
    content: '',
    onUpdate({ editor }) {
      const html = editor.getHTML()
      if (getPlainTextLength(html) <= 5000) {
        setForm(prev => ({ ...prev, emailSignature: html }))
      }
    },
  })

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/sign/email/branding`)
      .then(r => r.json())
      .then(r => {
        if (r.branding) {
          setForm({
            emailHeader: r.branding.emailHeader ?? '',
            emailSignature: r.branding.emailSignature ?? '',
            logo: r.branding.logo ?? '',
          })
          if (r.branding.logo) setLogoFileName(t('fileLoaded'))
          setEditorReady(true)
        } else {
          setEditorReady(true)
        }
      })
  }, [])

  useEffect(() => {
    if (editor && editorReady && form.emailSignature) {
      editor.commands.setContent(form.emailSignature)
    }
  }, [editorReady, editor])

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  async function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 3 * 1024 * 1024) {
      showKAlert('danger', t('logoFileTooLarge'))
      e.target.value = ''
      return
    }
    const base64 = await fileToBase64(file)
    setLogoFileName(file.name)
    setForm(prev => ({ ...prev, logo: base64 }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_API_URL}/sign/email/branding`,
        'POST',
        form
      )
      if (response?.message?.variant === 'success') {
        showKAlert('success', t('emailBrandingUpdated'))
      } else {
        showKAlert('danger', response?.message?.content ?? t('emailBrandingError'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Row className='mt-4'>
      <Col lg={4}>
        <div className='d-flex justify-between section-title-blade'>
          <div className='pe-4 sm:px-0'>
            <p className='mt-1 text-draft'>{t('emailBranding')}</p>
          </div>
        </div>
      </Col>
      <Col lg={8}>
        <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
          <Form onSubmit={handleSubmit}>
            <Form.Group className='mb-3'>
              <Form.Label className='fw-medium smaller text-primary'>{t('emailHeader')}</Form.Label>
              <Form.Control
                type='text'
                name='emailHeader'
                value={form.emailHeader}
                onChange={handleChange}
                maxLength={255}
              />
              <div className='text-end small text-muted mt-1'>{form.emailHeader.length} / 255</div>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-medium smaller text-primary'>
                {t('emailSignature')}
              </Form.Label>
              <TiptapToolbar editor={editor} />
              <EditorContent
                editor={editor}
                className='border rounded-bottom p-2'
                style={{ minHeight: 120 }}
              />
              <div className='text-end small text-muted mt-1'>
                {getPlainTextLength(form.emailSignature)} / 5000
              </div>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-medium smaller text-primary'>{t('logo')}</Form.Label>
              <div className='d-flex align-items-center gap-2'>
                <KButton
                  label={t('uploadLogo')}
                  icon='image'
                  variant='outline-secondary'
                  onClick={() => logoInputRef.current?.click()}
                />
                {logoFileName && <span className='text-muted small'>{logoFileName}</span>}
              </div>
              <input
                ref={logoInputRef}
                type='file'
                accept='image/*'
                className='d-none'
                onChange={handleLogoChange}
              />
            </Form.Group>
            <KButton type='submit' label={t('save')} variant='primary' disabled={loading} />
          </Form>
        </div>
      </Col>
    </Row>
  )
}
